import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Heart, MapPin, Phone, MessageCircle, ShieldCheck, Camera, Video, Star, ChevronLeft, Send, Flag, Ban, QrCode, BedDouble, Fuel, Car, Calendar, Gauge, Settings2, Tag } from "lucide-react";
import EquipementsBadges from "@/components/EquipementsBadges";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { fetchAnnonce, fetchSimilar, startConversation, toggleFavoriteRemote, incrementView, trackContactClick, markAnnonceSold, deleteMyAnnonce } from "@/lib/api";
import { fmtPrice, isFavorite, toggleFavorite, withWatermark, energieLabel, boiteLabel, cylindreeLabel, fmtKm } from "@/lib/utils";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { logger } from "@/lib/logger";
import AnnonceCard from "@/components/AnnonceCard";
import YangoDelivery from "@/components/YangoDelivery";
import LocationMap from "@/components/LocationMap";
import SellerProfileBadge from "@/components/SellerProfileBadge";
import TrustBanner from "@/components/TrustBanner";
import DispoCalendar from "@/components/DispoCalendar";
import CompareToggleButton from "@/components/CompareToggleButton";
import { SafetyGate } from "@/components/SafetyGate";
import { EscrowBuyPanel } from "@/components/EscrowBuyPanel";
import { SoldSurveyModal } from "@/components/SoldSurveyModal";
import { FinancingBanner } from "@/components/FinancingBanner";
import { WhatsAppShareButton } from "@/components/WhatsAppShareButton";

export default function AnnonceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [a, setA] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [fav, setFav] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [sold, setSold] = useState(null);
  const isOwnerUser = !!user && user.user_id === a?.owner_user_id;
  const onSold = async () => {
    try { const r = await markAnnonceSold(a.id); setA((x) => ({ ...x, is_sold: true })); setSold({ surveyDone: r.survey_done }); }
    catch (err) { toast.error(err.response?.data?.detail || "Echec"); }
  };
  const [showQR, setShowQR] = useState(false);
  const [lang, setLang] = useState("fr");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setNotFound(false);
    fetchAnnonce(id).then((d) => {
      setA(d);
      setFav(isFavorite(d.id));
    }).catch(() => setNotFound(true));
    fetchSimilar(id).then(setSimilar).catch(() => {});
    // Fire-and-forget view counter (de-duplicated server-side per day)
    incrementView(id).catch(() => {});
  }, [id]);

  const handleFav = async () => {
    if (!a) return;
    toggleFavorite(a.id);
    setFav(!fav);
    if (user) {
      try { await toggleFavoriteRemote(a.id); }
      catch (e) { logger.warn("Favorite sync failed", e); }
    }
  };

  const handleContact = async () => {
    if (!user) { login(); return; }
    if (!msg.trim()) return;
    setSending(true);
    try {
      const { conversation_id } = await startConversation({ annonce_id: a.id, text: msg.trim() });
      navigate(`/messages/${conversation_id}`);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur a l'envoi du message.");
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="annonce-notfound">
        <h2 className="text-2xl font-extrabold mb-2">Annonce introuvable</h2>
        <p className="text-[var(--ink-soft)] mb-6">Cette annonce n'existe pas ou a ete supprimee.</p>
        <Link to="/annonces" className="btn-primary">Retour aux annonces</Link>
      </main>
    );
  }
  if (!a) return <main className="max-w-7xl mx-auto px-6 py-20" data-testid="annonce-loading">Chargement...</main>;

  const gallery = [a.image, ...(a.images || [])];

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="annonce-detail-page">
      <Link to="/annonces" className="inline-flex items-center gap-1 text-orange-600 font-semibold mb-5" data-testid="back-to-annonces">
        <ChevronLeft size={16} /> Retour aux annonces
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8">
        <div>
          <div className="rounded-[24px] overflow-hidden">
            <img src={withWatermark(gallery[activeImg] || a.image)} alt={a.title} className="w-full h-[460px] object-cover" />
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 grid grid-cols-4 gap-3">
              {gallery.slice(0, 4).map((g, i) => (
                <button
                  key={`thumb-${g}-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`overflow-hidden rounded-2xl border-2 ${activeImg === i ? "border-orange-500" : "border-transparent"}`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={withWatermark(g)} alt="" className="w-full h-24 object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <aside className="glass-card p-7 h-fit sticky top-24">
          <div className="flex flex-wrap gap-2 mb-4">
            {a.is_sold && <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-600 text-white" data-testid="annonce-sold-badge">Vendu</span>}
            {a.premium && <span className="badge-premium">Premium</span>}
            {a.verified && <span className="badge-verified">Annonce verifiee</span>}
            {a.tag && <span className="badge-premium" style={{ background: "linear-gradient(135deg,#ffe4cc,#ffb37a)", color: "#9a3412" }}>{a.tag}</span>}
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight leading-tight" data-testid="annonce-title">
            {(lang !== "fr" && a.translations?.[lang]?.title) || a.title}
          </h1>
          {a.translations && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="lang-switcher">
              {[["fr", "Français"], ["wo", "Wolof"], ["bm", "Bambara"]].map(([code, label]) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setLang(code)}
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition ${
                    lang === code
                      ? "bg-orange-500 text-white border-orange-500"
                      : "border-[var(--line)] text-[var(--ink-soft)] hover:border-orange-300"
                  }`}
                  data-testid={`lang-chip-${code}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {a.ref ? (
            <div className="mt-1 text-xs font-bold tracking-wider text-orange-600 inline-flex items-center gap-1.5" data-testid="annonce-ref">
              <span className="bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200">Ref : {a.ref}</span>
            </div>
          ) : null}
          <div className="mt-3 text-orange-600 font-extrabold text-2xl" data-testid="annonce-price">
            {fmtPrice(a.price, a.price_unit)}
          </div>
          {a.surface_m2 && a.price ? (
            <div className="text-xs font-semibold text-orange-700 mt-1" data-testid="price-per-m2">
              {a.surface_m2} m² · ~{Math.round(a.price / a.surface_m2).toLocaleString("fr-FR")} FCFA/m²
            </div>
          ) : null}
          <PriceHistoryBadge history={a.price_history} currentPrice={a.price} />
          {(a.marque || a.annee || a.energie || a.kilometrage != null || a.boite || a.cylindree) ? (
            <div className="mt-2 flex flex-wrap gap-2" data-testid="annonce-vehicule-specs">
              {a.marque && <SpecChip icon={Car} label={a.marque} testid="annonce-marque" />}
              {a.cylindree && cylindreeLabel(a.cylindree) && <SpecChip icon={Gauge} label={cylindreeLabel(a.cylindree)} testid="annonce-cylindree" />}
              {a.annee && <SpecChip icon={Calendar} label={a.annee} testid="annonce-annee" />}
              {a.energie && energieLabel(a.energie) && <SpecChip icon={Fuel} label={energieLabel(a.energie)} testid="annonce-energie" />}
              {a.kilometrage != null && a.kilometrage !== "" && <SpecChip icon={Gauge} label={fmtKm(a.kilometrage)} testid="annonce-kilometrage" />}
              {a.boite && boiteLabel(a.boite) && <SpecChip icon={Settings2} label={boiteLabel(a.boite)} testid="annonce-boite" />}
            </div>
          ) : null}
          <div className="mt-2 flex items-center gap-2 text-sm text-[var(--ink-soft)]">
            <MapPin size={14} /> {a.category} - {a.city}{a.quartier ? `, ${a.quartier}` : ""}
          </div>
          {typeof a.views === "number" && a.views > 0 && (
            <div className="mt-2 text-xs text-[var(--ink-soft)]" data-testid="annonce-views">
              👁 {a.views.toLocaleString("fr-FR")} {a.views === 1 ? "vue" : "vues"}
            </div>
          )}

          <div className="mt-6 grid grid-cols-3 gap-3">
            {a.phone && (
              <SafetyGate
                dataTestid="contact-phone-gate"
                onProceed={() => { trackContactClick(a.id); window.location.href = `tel:${a.phone}`; }}
              >
                <button type="button" className="btn-primary inline-flex items-center justify-center gap-2 w-full" data-testid="contact-phone-btn">
                  <Phone size={16} /> Appeler
                </button>
              </SafetyGate>
            )}
            <SafetyGate
              dataTestid="contact-whatsapp-gate"
              onProceed={() => {
                const num = (a.whatsapp || a.phone || "").replace(/\D/g, "");
                trackContactClick(a.id);
                window.open(`https://wa.me/${num}`, "_blank", "noreferrer");
              }}
            >
              <button type="button" className="btn-green inline-flex items-center justify-center gap-2 w-full" data-testid="contact-whatsapp-btn">
                <MessageCircle size={16} /> WhatsApp
              </button>
            </SafetyGate>
            <button
              type="button"
              onClick={() => setShowQR((v) => !v)}
              className="qr-blink inline-flex items-center justify-center gap-2 w-full text-base font-bold px-3 py-2.5 rounded-full border-2 border-orange-400 text-orange-600 bg-white hover:bg-orange-50 transition-colors"
              data-testid="qr-toggle-btn"
            >
              <QrCode size={18} /> QR code
            </button>
          </div>

          <WhatsAppShareButton annonce={a} className="mt-3" />

          {isOwnerUser && !a.is_sold && (
            <button type="button" onClick={onSold} className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-emerald-500 text-emerald-700 font-bold py-2.5 hover:bg-emerald-50" data-testid="annonce-sold-btn">
              <Tag size={16} /> Marquer comme vendu
            </button>
          )}
          {sold && <SoldSurveyModal annonce={a} surveyDone={sold.surveyDone} onClose={() => setSold(null)} onDelete={async () => { await deleteMyAnnonce(a.id); toast.success("Annonce supprimee"); navigate("/mes-annonces"); }} />}
          {!a.is_sold && <EscrowBuyPanel annonce={a} />}

          <FinancingBanner price={a.price} articleRef={a.ref || ""} country={a.country || "CI"} />

          {showQR && (
            <div className="mt-3 glass-card p-5 text-center" data-testid="qr-panel">
              <div className="text-sm font-bold text-[var(--ink-soft)] mb-2">Partagez cette annonce</div>
              <QRCodeSVG
                value={`${window.location.origin}/annonces/${a.ref || a.id}`}
                size={160} bgColor="#ffffff" fgColor="#1a1a1a" includeMargin
                className="mx-auto"
              />
              <div className="mt-2 text-[10px] text-[var(--ink-soft)] break-all" data-testid="qr-url">
                {`${window.location.origin}/annonces/${a.ref || a.id}`}
              </div>
            </div>
          )}

          <button
            onClick={() => { if (!showContact) trackContactClick(a.id); setShowContact((v) => !v); }}
            className="mt-3 w-full btn-secondary inline-flex items-center justify-center gap-2"
            data-testid="contact-seller-btn"
          >
            <Send size={14} /> Contacter le vendeur
          </button>
          {showContact && (
            <div className="mt-3 p-4 border border-[var(--line)] rounded-2xl" data-testid="contact-seller-panel">
              {!user && (
                <div className="text-xs text-[var(--ink-soft)] mb-2">
                  Connectez-vous pour envoyer un message au vendeur. <button onClick={login} className="text-orange-600 font-semibold underline" data-testid="contact-login-link">Se connecter</button>
                </div>
              )}
              <textarea
                rows={3}
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="Bonjour, votre annonce m'interesse..."
                data-testid="contact-message-input"
              />
              <button
                onClick={handleContact}
                disabled={!msg.trim() || sending}
                className="btn-primary w-full mt-2 inline-flex items-center justify-center gap-2"
                data-testid="contact-send-btn"
              >
                <Send size={14} /> {sending ? "Envoi..." : user ? "Envoyer le message" : "Se connecter et envoyer"}
              </button>
            </div>
          )}

          <button onClick={handleFav} className={`mt-3 w-full btn-secondary inline-flex items-center justify-center gap-2 ${fav ? "!text-red-500 !border-red-300" : ""}`} data-testid="fav-toggle-btn">
            <Heart size={16} fill={fav ? "#ef4444" : "none"} /> {fav ? "Retire des favoris" : "Ajouter aux favoris"}
          </button>

          <CompareToggleButton annonceId={a.id} ref={a.ref} title={a.title} />

          {a.owner_is_pro && (
            <div className="mt-3 inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-950 text-xs font-extrabold px-3 py-1.5 rounded-full shadow-md self-start" data-testid="detail-pro-badge">
              👑 Vendeur Fodindin PRO
            </div>
          )}
          {a.owner_user_id && <SellerProfileBadge userId={a.owner_user_id} />}

          <div className="mt-6 border-t border-[var(--line)] pt-5">
            <div className="font-bold mb-3">Avantages premium</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <span className="chip"><Camera size={12} /> 6 photos</span>
              <span className="chip"><Video size={12} /> Video 10 s</span>
              <span className="chip"><Star size={12} /> Mise en avant</span>
              <span className="chip"><ShieldCheck size={12} /> Verifiee</span>
            </div>
          </div>
        </aside>
      </div>

      <div className="mt-6">
        <TrustBanner />
      </div>

      {a.category === "Immobilier" && ["Maison meublee", "Studio", "Appartement"].includes(a.subcategory) && (
        <DispoCalendar
          annonceId={a.id}
          isOwner={!!user && (user.user_id === a.owner_user_id || user.is_admin)}
          whatsappNumber={a.whatsapp || a.phone || ""}
          annonceTitle={a.title}
          priceLabel={a.price ? `${a.price.toLocaleString("fr-FR")} FCFA${a.price_unit || ""}` : ""}
        />
      )}

      <section className="mt-10 glass-card p-7">
        <h2 className="font-bold text-lg mb-2">Description {lang !== "fr" && a.translations?.[lang] ? `(${lang === "wo" ? "Wolof" : "Bambara"})` : ""}</h2>
        <p className="text-[var(--ink)] leading-relaxed whitespace-pre-line" data-testid="annonce-description">
          {(lang !== "fr" && a.translations?.[lang]?.description) || a.description}
        </p>
      </section>

      {a.equipements && a.equipements.length > 0 && (
        <section className="mt-10 glass-card p-7" data-testid="annonce-equipements-section">
          <h2 className="font-bold text-lg mb-4 inline-flex items-center gap-2">
            <span className="w-8 h-8 rounded-xl bg-emerald-100 grid place-items-center text-emerald-700">
              <BedDouble size={16} />
            </span>
            Equipements
          </h2>
          <EquipementsBadges keys={a.equipements} variant="full" />
        </section>
      )}

      {/* Yango Delivery section */}
      <YangoDelivery annonceId={a.id} annonceCategory={a.category} annonceCity={a.city} />

      {/* Location map */}
      <LocationMap city={a.city} quartier={a.quartier} />

      {/* Footer actions: Signaler / Bloquer (admin) / QR code */}
      <ModerationFooter annonce={a} user={user} onBlocked={(blocked) => setA((prev) => ({ ...prev, blocked }))} />

      {similar.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold tracking-tight mb-1">Annonces similaires</h2>
          <p className="text-[var(--ink-soft)] mb-6 text-sm">Memes categories, autres villes.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {similar.map((s) => <AnnonceCard key={s.id} a={s} />)}
          </div>
        </section>
      )}
    </main>
  );
}

function PriceHistoryBadge({ history, currentPrice }) {
  if (!Array.isArray(history) || history.length === 0) return null;
  // Pick the most relevant "drop" — first historical price > current price
  const sorted = [...history].sort((a, b) => new Date(a.at) - new Date(b.at));
  const firstSeen = sorted[0]?.from;
  if (typeof firstSeen !== "number" || firstSeen <= currentPrice) return null;
  const dropPct = Math.round(((firstSeen - currentPrice) / firstSeen) * 100);
  const last = sorted[sorted.length - 1];
  const ago = relativeTime(last?.at);
  const fmt = (n) => new Intl.NumberFormat("fr-FR").format(n);
  return (
    <div
      className="mt-2 inline-flex items-center gap-2 text-xs font-bold bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-full"
      data-testid="price-history-badge"
      title={`Historique: ${history.length} variation${history.length > 1 ? "s" : ""}`}
    >
      <span className="line-through text-green-600/70">{fmt(firstSeen)} FCFA</span>
      <span>→</span>
      <span>Baisse de {dropPct}%</span>
      {ago ? <span className="text-green-600/70 font-medium">· {ago}</span> : null}
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return "a l'instant";
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `il y a ${d}j`;
  const mo = Math.floor(d / 30);
  return `il y a ${mo} mois`;
}

function ModerationFooter({ annonce, user, onBlocked }) {
  const [showReport, setShowReport] = useState(false);
  const [reason, setReason] = useState("");
  const [reporting, setReporting] = useState(false);
  const [blocking, setBlocking] = useState(false);

  const submitReport = async () => {
    setReporting(true);
    try {
      await axios.post(`${API}/annonces/${annonce.id}/report`, { reason }, { withCredentials: true });
      toast.success("Signalement envoye. Merci, notre equipe va examiner cette annonce.");
      setShowReport(false); setReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Echec du signalement");
    } finally { setReporting(false); }
  };

  const toggleBlock = async () => {
    if (!annonce.blocked && !window.confirm(`Bloquer l'annonce ${annonce.ref || ""} ?\nElle ne sera plus visible par le public.`)) return;
    setBlocking(true);
    try {
      const newState = !annonce.blocked;
      await axios.post(`${API}/admin/annonces/${annonce.id}/block`, { blocked: newState }, { withCredentials: true });
      onBlocked?.(newState);
      toast.success(newState ? "Annonce bloquee" : "Annonce debloquee");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Echec de l'action admin");
    } finally { setBlocking(false); }
  };

  return (
    <section className="mt-12 border-t border-[var(--line)] pt-6" data-testid="moderation-footer">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowReport((v) => !v)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-red-200 text-red-600 hover:bg-red-50 transition"
            data-testid="report-toggle-btn"
          >
            <Flag size={14} /> Signaler cette annonce
          </button>

          {user?.is_admin && (
            <button
              type="button"
              onClick={toggleBlock}
              disabled={blocking}
              className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border transition ${
                annonce.blocked
                  ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                  : "border-gray-800 bg-gray-800 text-white hover:bg-gray-900"
              }`}
              data-testid="admin-block-btn"
            >
              <Ban size={14} /> {annonce.blocked ? "Debloquer (admin)" : "Bloquer (admin)"}
            </button>
          )}

          {annonce.blocked && (
            <span className="inline-flex items-center gap-1 text-xs font-bold bg-red-100 text-red-700 px-3 py-1 rounded-full" data-testid="blocked-badge">
              <Ban size={11} /> BLOQUEE
            </span>
          )}
        </div>
      </div>

      {showReport && (
        <div className="mt-4 glass-card p-5 max-w-xl" data-testid="report-form">
          <p className="text-sm font-semibold mb-2">Pourquoi signalez-vous cette annonce ?</p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Prix suspect, contenu frauduleux, doublon, etc. (optionnel)"
            className="w-full !py-2.5 !px-3 rounded-xl border border-[var(--line)] focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none transition bg-white"
            data-testid="report-reason"
          />
          <div className="mt-3 flex gap-2 justify-end">
            <button type="button" onClick={() => { setShowReport(false); setReason(""); }} className="btn-secondary text-sm" data-testid="report-cancel">
              Annuler
            </button>
            <button type="button" onClick={submitReport} disabled={reporting} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="report-submit">
              <Flag size={14} /> {reporting ? "Envoi..." : "Envoyer le signalement"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function SpecChip({ icon: Icon, label, testid }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-1 rounded-full" data-testid={testid}>
      <Icon size={12} /> {label}
    </span>
  );
}
